import { type FC } from 'react'
import Box from '@mui/material/Box'
import { useTranslation } from 'react-i18next'

import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SimpleImage } from 'components/common/SimpleImage'
import { FloorPlanColors, Orientations, Styles, getFloorPlanPreviewImages, getPreview } from 'constants/floorPlan'

/** The overlays shown on top of either preview. */
export interface FloorPlanPreviewOverlays {
  showFixtures?: boolean
  showFurniture?: boolean
  showRoomNames?: boolean
  showScalebar?: boolean
  showCompass?: boolean
  showDisclaimer?: boolean
  /** Whether a company logo is chosen; the placeholder is shown only with one. */
  showLogo?: boolean
  /** Dimension chains and room areas are shown only while dimensions are active. */
  isActiveDimensions?: boolean
  showDimensionChain?: boolean
  showRoomArea?: boolean
}

const dimensionOverlays = ({ isActiveDimensions, showDimensionChain, showRoomArea }: FloorPlanPreviewOverlays) =>
  isActiveDimensions ? { showDimensionChain, showRoomArea } : { showDimensionChain: false, showRoomArea: false }

const PreviewStack: FC<{ urls: string[], keySuffix?: string }> = ({ urls, keySuffix = '' }) => {
  const { t } = useTranslation(['purchase_flow'])

  return (
    <SectionedBorderBox title={t('floor_plan_step.preview.example_picture')}>
      <Box sx={{ position: 'relative', width: '100%', height: '23rem' }}>
        {urls.map((url) =>
          <SimpleImage
            key={`${url}${keySuffix}`}
            url={url}
            sx={{ top: 0, left: 0, width: '100%', height: '100%', position: 'absolute' }}
            borderRadius="none"
          />
        )}
      </Box>
    </SectionedBorderBox>
  )
}

interface Preview2DProps extends FloorPlanPreviewOverlays {
  /** Colour theme; anything that is not a known colour falls back to black. */
  color?: string | null
  /** Re-mounts the images when another template is picked. */
  templateId?: string | null
}

/**
 * Live preview of the 2D floor-plan settings.
 *
 * @example
 * <FloorPlanPreview2D color={FloorPlanColors.BLUE} showFurniture />
 */
export const FloorPlanPreview2D: FC<Preview2DProps> = ({ color, templateId, ...overlays }) => {
  const urls = getFloorPlanPreviewImages({
    ...overlays,
    ...dimensionOverlays(overlays),
    color: Object.values(FloorPlanColors).includes(color as FloorPlanColors) ? color as FloorPlanColors : FloorPlanColors.BLACK,
  })

  return <PreviewStack urls={urls} keySuffix={`-${templateId || 'basic-2D'}`} />
}

interface Preview3DProps extends FloorPlanPreviewOverlays {
  /** 3D style; anything that is not a known style falls back to the first. */
  style?: string | null
  orientation?: Orientations | null
}

/**
 * Live preview of the 3D floor-plan settings.
 *
 * @example
 * <FloorPlanPreview3D style={Styles.STYLE_2} orientation={Orientations.ANGLE_VIEW} />
 */
export const FloorPlanPreview3D: FC<Preview3DProps> = ({ style, orientation, ...overlays }) => {
  const urls = getPreview({
    ...overlays,
    ...dimensionOverlays(overlays),
    style: Object.values(Styles).includes(style as Styles) ? style as Styles : Styles.STYLE_1,
    orientation: orientation ?? Orientations.BIRD_VIEW,
  })

  return <PreviewStack urls={urls} />
}
